import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Phone, ArrowLeft, ArrowRight, Calendar, Clock, Stethoscope, BadgeCheck } from 'lucide-react';
import { CLINIC, IMAGES, BLOGS } from '../mock';
import { PageHero } from '../components/Shared';

export default function BlogPost() {
  const { id } = useParams();
  const post = BLOGS.find((b) => b.id === id);
  if (!post) return <Navigate to="/blogs" replace />;

  const related = BLOGS.filter((b) => b.id !== id).slice(0, 3);

  return (
    <div>
      <PageHero eyebrow={post.category} title={post.title} subtitle={post.excerpt} image={IMAGES[post.image]} />

      <article className="py-16">
        <div className="container-x max-w-3xl">
          <Link to="/blogs" className="inline-flex items-center gap-2 text-teal-700 text-sm font-medium mb-6 hover:gap-3 transition-all"><ArrowLeft size={16} /> All Blogs</Link>

          <div className="flex flex-wrap items-center gap-4 text-sm text-slate-400 mb-8 pb-6 border-b border-teal-50">
            <span className="flex items-center gap-1"><Calendar size={14} /> {post.date}</span>
            <span className="flex items-center gap-1"><Clock size={14} /> {post.read}</span>
            <span className="inline-block bg-teal-50 text-teal-700 text-xs font-semibold px-3 py-1 rounded-full">{post.category}</span>
          </div>

          {post.content ? (
            <div className="space-y-5">
              {post.content.map((blk, i) =>
                blk.t === 'h' ? (
                  <h2 key={i} className="font-display text-2xl text-teal-900 pt-4" style={{ fontWeight: 600 }}>{blk.x}</h2>
                ) : (
                  <p key={i} className="text-slate-700 leading-relaxed text-[17px]">{blk.x}</p>
                )
              )}
            </div>
          ) : (
            <p className="text-slate-700 leading-relaxed text-[17px]">{post.excerpt}</p>
          )}

          {/* Author */}
          {post.author && (
            <div className="mt-10 flex items-center gap-4 bg-teal-50/60 border border-teal-100 rounded-2xl p-5">
              <span className="grid place-items-center w-12 h-12 rounded-full bg-teal-600 text-white shrink-0"><BadgeCheck size={24} /></span>
              <div>
                <div className="font-semibold text-teal-900">{post.author}</div>
                <div className="text-slate-500 text-sm">{post.authorTitle}</div>
              </div>
            </div>
          )}

          {/* CTA */}
          <div className="deep-section rounded-3xl p-8 text-center mt-12">
            <span className="grid place-items-center w-14 h-14 rounded-2xl bg-white/10 text-white mx-auto mb-4"><Stethoscope size={26} /></span>
            <h3 className="font-display text-2xl text-white mb-2" style={{ fontWeight: 600 }}>Have questions about your treatment?</h3>
            <p className="text-teal-100/85 mb-6 max-w-xl mx-auto">Speak to Dr Puneet Srivastava for evidence-based, personalised rheumatology care across Ghaziabad, Agra, Moradabad &amp; Aligarh.</p>
            <div className="flex flex-wrap justify-center gap-3">
              <a href={`tel:${CLINIC.phoneRaw}`} className="btn-coral inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold"><Phone size={18} /> Call {CLINIC.phone}</a>
              <Link to="/second-opinion" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold bg-white/10 text-white hover:bg-white/20 transition-colors">Book a Consultation <ArrowRight size={18} /></Link>
            </div>
          </div>

          {/* Related */}
          <h3 className="font-display text-xl text-teal-900 mt-14 mb-5" style={{ fontWeight: 600 }}>More from the blog</h3>
          <div className="grid sm:grid-cols-3 gap-4">
            {related.map((r) => (
              <Link key={r.id} to={`/blogs/${r.id}`} className="card-lift block bg-white rounded-2xl border border-teal-50 overflow-hidden">
                <div className="h-28 overflow-hidden"><img src={IMAGES[r.image]} alt={r.title} className="w-full h-full object-cover" /></div>
                <div className="p-4">
                  <span className="text-xs font-semibold text-teal-600 uppercase tracking-wide">{r.category}</span>
                  <p className="text-teal-900 font-medium mt-1 leading-snug text-sm">{r.title}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </article>
    </div>
  );
}
